<template>
  <div class="container-fluid">
    <div v-if="indication" class="mt-4">
      <div class="card mb-4">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h5 class="mb-0">Dados da Indicação</h5>

          <div v-if="canDeclineIndication">
            <button
              v-if="declinable"
              class="btn btn-outline btn-sm"
              :disabled="accountIsClosed"
              @click="showDeclineModal = true"
            >
              Não quero indicar
            </button>
            <button
              v-if="undoable"
              class="btn btn-outline btn-sm"
              :disabled="accountIsClosed"
              @click="showUndoDeclineModal = true"
            >
              Desfazer não indicação
            </button>
          </div>
        </div>

        <div class="card-body">
          <div class="row">
            <div v-for="field in summaryFields" :key="field.label" class="col-md-3 mb-3">
              <small class="text-muted d-block">{{ field.label }}</small>
              <span>{{ field.value }}</span>
            </div>
          </div>

          <div v-if="declined" class="declined-box">
            <strong>Não indicado por decisão do cliente</strong>
            <p class="mb-1 mt-2"><strong>Motivo:</strong> {{ indication.declineReason || 'Não informado' }}</p>
            <p class="mb-1"><strong>Por:</strong> {{ indication.declinedBy || '-' }}</p>
            <p class="mb-0"><strong>Em:</strong> {{ formatDateTime(indication.declinedAt) }}</p>
          </div>
        </div>
      </div>

      <div class="card mb-4">
        <div
          class="card-header d-flex justify-content-between align-items-center cursor-pointer"
          @click="showHistory = !showHistory"
        >
          <h5 class="mb-0">Histórico de Atualizações da Indicação</h5>
          <eye-icon v-if="showHistory" size="1.5x" class="text-primary"/>
          <eye-off-icon v-else size="1.5x" class="text-muted"/>
        </div>

        <div v-if="showHistory" class="card-body">
          <div class="mb-2">
            <small>{{ historyInfo }}</small>
          </div>

          <div class="table-responsive">
            <table class="table table-borderless table-border table-align">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Ação</th>
                  <th>Status</th>
                  <th>Autor</th>
                  <th>Motivo</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="history in paginatedHistories" :key="history.id">
                  <td>{{ formatDateTime(history.createdAt) }}</td>
                  <td>{{ translateHistoryAction(history.action) }}</td>
                  <td>{{ statusChange(history) }}</td>
                  <td>{{ translateAuthor(history.author) }}</td>
                  <td>{{ history.reason || '-' }}</td>
                </tr>
                <tr v-if="histories.length === 0">
                  <td colspan="5" class="text-center text-muted">
                    Nenhum histórico de atualização encontrado
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="histories.length > historyPerPage" class="row">
            <div class="col-md-12 my-1">
              <Pagination
                v-model="historyCurrentPage"
                :total-count="histories.length"
                :total-pages="historyTotalPages"
                @pagination="historyCurrentPage = $event"
              />
            </div>
          </div>
        </div>
      </div>

      <DeclineIndicationModal
        v-model="showDeclineModal"
        :indications="declineIndications"
        @finished="handleDeclineFinished"
      />

      <Modal v-model="showUndoDeclineModal" width="md">
        <span slot="header">Desfazer não indicação</span>

        <p>
          A multa <strong>{{ ait }}</strong> voltará para <strong>Aguardando Indicação</strong>
          e poderá ser indicada normalmente.
        </p>
        <p class="mb-0">Deseja continuar?</p>

        <div slot="footer">
          <button class="btn btn-outline mr-2" @click="showUndoDeclineModal = false">
            Cancelar
          </button>
          <button class="btn btn-primary" @click="undoDecline">
            Confirmar
          </button>
        </div>
      </Modal>
    </div>

    <div v-else class="mt-4">
      <div class="card">
        <div class="card-body text-center">
          <span v-if="loading">Carregando...</span>
          <span v-else>Registro não encontrado</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { EyeIcon, EyeOffIcon } from 'vue-feather-icons'
import EntityProxy from '@/models/EntityProxy'
import GetAccount from '@/mixins/GetAccount.vue'
import GlobalLoading from '@/mixins/GlobalLoading'
import DriverIndicationStatuses from '@/mixins/DriverIndicationStatuses'
import Pagination from '@/components/shares/specialTag/Pagination'
import Modal from '@/components/shares/specialTag/Modal'
import DeclineIndicationModal from '@/components/driver-indication/DeclineIndicationModal.vue'
import Formatter from '@/models/Formatter'
import {
  DECLINED,
  canDecline,
  canUndoDecline,
  translateHistoryAction
} from '@/utils/driverIndicationStatus'
import { mapMutations } from 'vuex'

export default {
  components: { EyeIcon, EyeOffIcon, Pagination, Modal, DeclineIndicationModal },
  computed: {
    ticket() {
      return this.indication.ticket || {}
    },
    ait() {
      return this.ticket.ait || '-'
    },
    status() {
      return this.findStatus(this.indication)
    },
    declined() {
      return Boolean(this.status) && this.status.code === DECLINED
    },
    canDeclineIndication() {
      return this.$canNew('driver-indication', 'decline_indication')
    },
    declinable() {
      return canDecline(this.status, this.ticket.indicationDeadline)
    },
    undoable() {
      return canUndoDecline(this.status, this.ticket.indicationDeadline)
    },
    declineIndications() {
      return [this.indication]
    },
    summaryFields() {
      return [
        { label: 'AIT', value: this.ait },
        { label: 'Placa', value: this.ticket.plate || '-' },
        { label: 'Renavam', value: this.ticket.renavam || '-' },
        { label: 'CNPJ', value: this.formatCnpj(this.ticket.cnpj) },
        { label: 'Data da Infração', value: this.formatDateTime(this.ticket.occurredAt) },
        { label: 'Prazo de Indicação', value: this.formatDate(this.ticket.indicationDeadline) },
        { label: 'Status', value: this.status ? this.status.friendly_name : '-' },
        { label: 'Protocolo de encaminhamento para indicação', value: this.indication.protocol || '-' }
      ]
    },
    historyInfo() {
      const total = this.histories.length
      if (total === 0) return 'Nenhum registro encontrado'

      const start = (this.historyCurrentPage - 1) * this.historyPerPage + 1
      const end = Math.min(this.historyCurrentPage * this.historyPerPage, total)
      return `Mostrando de ${start} até ${end} de ${total} registros`
    },
    historyTotalPages() {
      return Math.ceil(this.histories.length / this.historyPerPage)
    },
    paginatedHistories() {
      const start = (this.historyCurrentPage - 1) * this.historyPerPage
      const end = start + this.historyPerPage
      return this.histories.slice(start, end)
    }
  },
  created() {
    this.setNameBreadcrumb('...')
    this.loadIndication()
  },
  data: () => ({
    indication: null,
    histories: [],
    loading: true,
    showHistory: true,
    showDeclineModal: false,
    showUndoDeclineModal: false,
    historyCurrentPage: 1,
    historyPerPage: 10
  }),
  methods: {
    ...mapMutations(['setNameBreadcrumb']),
    translateHistoryAction,
    loadIndication() {
      this.loading = true

      return Promise.all([
        this.axios.get(`maestro/driver_indications/${this.id}`),
        this.loadDriverIndicationStatuses()
      ])
        .then(([response]) => {
          this.indication = EntityProxy.build(response.data)
          this.setNameBreadcrumb(this.ait)
          return this.loadHistories()
        })
        .catch(e => this.$notify.addError(e))
        .finally(() => {
          this.loading = false
        })
    },
    loadHistories() {
      const params = { per_page: 1000 }

      return this.axios.get(`maestro/driver_indications/${this.id}/histories`, { params })
        .then(response => {
          this.histories = EntityProxy.build(response.data || [])
          this.historyCurrentPage = 1
        })
        .catch(() => {
          this.histories = []
        })
    },
    handleDeclineFinished({ succeeded }) {
      if (succeeded.length > 0) this.loadIndication()
    },
    undoDecline() {
      this.showUndoDeclineModal = false
      this.openGlobalLoad()

      this.axios.put(`maestro/driver_indications/${this.id}/undo_decline`)
        .then(() => {
          this.$notify.add('Não indicação desfeita. A multa voltou para Aguardando Indicação.')
          return this.loadIndication()
        })
        .catch(e => this.$notify.addError(e))
        .finally(() => this.closeGlobalLoad())
    },
    statusChange(history) {
      const from = history.statusFrom && history.statusFrom.friendly_name
      const to = history.statusTo && history.statusTo.friendly_name
      if (from && to) return `${from} → ${to}`
      return to || from || '-'
    },
    translateAuthor(author) {
      if (!author) return '-'
      return author === 'system' ? 'Sistema' : author
    },
    formatDate(value) {
      if (!value) return '-'
      return this.$options.filters.date(value)
    },
    formatDateTime(value) {
      if (!value) return '-'
      return this.$options.filters.datetime(value)
    },
    formatCnpj(value) {
      if (!value) return '-'
      return Formatter.cnpj(value)
    }
  },
  mixins: [GetAccount, GlobalLoading, DriverIndicationStatuses],
  name: 'DriverIndicationDetailsView',
  props: {
    id: { required: true }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.card {
  background-color: var(--white-bg, #ffffff);
  border: 1px solid var(--border-color, #dee2e6);
  border-radius: 8px;
}

.card-header {
  background-color: var(--white-bg, #ffffff);
  border-bottom: 1px solid var(--border-color, #dee2e6);
  padding: 1rem 1.5rem;
}

.card-header h5 {
  color: var(--primary-text-color, #212529);
}

.card-body {
  padding: 1.5rem;
}

.table-responsive {
  overflow-x: auto;
}

.declined-box {
  background-color: var(--card-bg);
  border: 1px solid var(--border-color);
  border-left: 4px solid #64748b;
  border-radius: 8px;
  padding: 16px;
  color: var(--primary-text-color);
}
</style>
